import { faUserPlus } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { ReactNode } from 'react';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { CARD_ASPECT_RATIO_SMALL, CARD_SIZE } from '@sorare/core/src/lib/cards';

import { cardShape } from '@sorare/us-sports/src/components/cards/CardShape';

const Placeholder = styled.div<{ dark: boolean }>`
  ${cardShape}
  --contrastingBackground: ${({ dark }) =>
    dark ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.2)'};
  background-color: var(--contrastingBackground);
  text-align: center;

  color: ${({ dark }) =>
    dark ? 'var(--c-static-neutral-600)' : 'var(--c-static-neutral-100)'};

  font-weight: bold;
  min-width: var(--double-and-a-half-unit);
  max-width: ${CARD_SIZE}px;
  position: relative;
`;

const Content = styled(Vertical).attrs({ center: true })`
  position: absolute;
  inset: 0;
  --iconSize: var(--triple-unit);
  padding: var(--half-unit);
  &.scalable {
    transform: scale(calc(1 / var(--scaleFactor, 1)));
  }
`;

const RoundIcon = styled(FontAwesomeIcon)`
  width: var(--iconSize) !important;
  height: var(--iconSize);
  display: grid;
  justify-content: center;
  align-content: center;
`;

const SlotName = styled.div`
  font-weight: var(--t-bold);
  letter-spacing: -0.06em;
  text-transform: uppercase;
  flex: none;
`;
const SVG = styled.svg`
  max-width: ${CARD_SIZE}px;
  width: 100%;
  height: 100%;
  vertical-align: bottom;
`;

type Props = {
  name: string;
  explanation?: ReactNode;
  dark?: boolean;
  className?: string;
};

export const PlaceholderCard = ({
  dark = false,
  name,
  explanation,
  scalable = false,
  className,
}: Props & { scalable?: boolean }) => {
  return (
    <Placeholder dark={dark} className={className}>
      <SVG viewBox="0 0 61 100" />
      <Content className={classNames({ scalable })}>
        <RoundIcon icon={faUserPlus} />
        <SlotName>{name}</SlotName>
        {explanation}
      </Content>
    </Placeholder>
  );
};

const PlaceholderSmall = styled.div<{ dark: boolean }>`
  ${cardShape}
  --background-color: ${({ dark }) =>
    dark ? `var(--c-static-neutral-800)` : 'var(--c-static-neutral-200)'};
  --color: ${({ dark }) => (dark ? `var(--c-static-neutral-100)` : `#73747f`)};
  --shadow: ${({ dark }) =>
    dark ? `none` : `0px 12px 40px rgba(35, 36, 41, 0.2);`};
  background-color: var(--background-color);
  box-shadow: var(--shadow);
  border-radius: var(--unit);
  color: var(--color);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  --iconSize: var(--double-unit);
  aspect-ratio: ${CARD_ASPECT_RATIO_SMALL};
  width: 100%;
  max-width: 90px;
`;

const RoundIconSmall = styled(FontAwesomeIcon)`
  width: var(--iconSize) !important;
  height: var(--iconSize);
`;
export const PlaceholderCardSmall = ({
  name,
  className,
  dark = false,
  explanation,
}: Props) => {
  return (
    <PlaceholderSmall className={className} title={name} dark={dark}>
      {!explanation && (
        <>
          <RoundIconSmall icon={faUserPlus} />
          <SlotName>{name}</SlotName>
        </>
      )}
      {explanation}
    </PlaceholderSmall>
  );
};
